import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api, clearToken, getToken, setToken, type User } from '../lib/api'

type AuthCtx = {
  user: User | null
  loading: boolean
  login: (email: string, password: string, remember?: boolean) => Promise<void>
  loginWithToken: (token: string) => Promise<void>
  register: (name: string, email: string, password: string) => Promise<void>
  logout: () => void
  refresh: () => Promise<void>
}

const Ctx = createContext<AuthCtx | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null)
      setLoading(false)
      return
    }
    try {
      const res = await api.me()
      setUser(res.user)
    } catch {
      clearToken()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const value = useMemo<AuthCtx>(
    () => ({
      user,
      loading,
      async login(email, password, remember = true) {
        const res = await api.login({ email, password })
        setToken(res.token, remember)
        setUser(res.user)
      },
      async loginWithToken(token) {
        setToken(token)
        setLoading(true)
        await refresh()
      },
      async register(name, email, password) {
        const res = await api.register({ name, email, password, agree_tos: true })
        setToken(res.token)
        setUser(res.user)
      },
      logout() {
        clearToken()
        setUser(null)
      },
      refresh,
    }),
    [user, loading, refresh],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth outside provider')
  return ctx
}
